import { useEffect, useState } from "react";

/**
 * Iter 123 — Hook para PWA standalone (POS Phase 4)
 *
 * Detecta se o app está rodando em modo standalone (instalado como PWA),
 * captura o evento `beforeinstallprompt` para CTA de instalação, e troca
 * o manifest dinamicamente quando montado (volta ao original ao desmontar).
 *
 * Usage:
 *   const { isStandalone, installPrompt, doInstall } = usePwaStandalone({
 *     manifest: "/manifest-pos.json",
 *     themeColor: "#0B0D0F",
 *   });
 *
 * - `isStandalone`: true quando display-mode = standalone (PWA instalado)
 * - `installPrompt`: o evento beforeinstallprompt (null se não disponível)
 * - `doInstall()`: dispara o prompt nativo
 * - `requestPersistence()`: pede storage persistente (não eviction de IDB)
 */
export function usePwaStandalone({ manifest = null, themeColor = null } = {}) {
  const [isStandalone, setIsStandalone] = useState(() => detectStandalone());
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);

  // Manifest + theme swap (preserva valores anteriores ao desmontar)
  useEffect(() => {
    if (!manifest && !themeColor) return;
    const link = document.querySelector('link[rel="manifest"]');
    const meta = document.querySelector('meta[name="theme-color"]');
    const prevManifest = link?.getAttribute("href");
    const prevTheme    = meta?.getAttribute("content");
    if (manifest && link) link.setAttribute("href", manifest);
    if (themeColor && meta) meta.setAttribute("content", themeColor);
    return () => {
      if (link && prevManifest) link.setAttribute("href", prevManifest);
      if (meta && prevTheme)    meta.setAttribute("content", prevTheme);
    };
  }, [manifest, themeColor]);

  // beforeinstallprompt + appinstalled + display-mode change
  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setInstallPrompt(e); };
    const onInstalled = () => { setInstalled(true); setInstallPrompt(null); setIsStandalone(true); };
    const mq = window.matchMedia("(display-mode: standalone)");
    const onMqChange = (e) => setIsStandalone(e.matches);

    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    if (mq.addEventListener) mq.addEventListener("change", onMqChange);
    else if (mq.addListener) mq.addListener(onMqChange);

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      if (mq.removeEventListener) mq.removeEventListener("change", onMqChange);
      else if (mq.removeListener) mq.removeListener(onMqChange);
    };
  }, []);

  const doInstall = async () => {
    if (!installPrompt) return false;
    installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    setInstallPrompt(null);
    return choice?.outcome === "accepted";
  };

  // Persistent storage para offline POS (evita eviction da IndexedDB)
  const requestPersistence = async () => {
    if (!navigator?.storage?.persist) return false;
    try {
      const already = await navigator.storage.persisted();
      if (already) return true;
      return await navigator.storage.persist();
    } catch { return false; }
  };

  return { isStandalone, installPrompt, installed, doInstall, requestPersistence };
}

function detectStandalone() {
  if (typeof window === "undefined") return false;
  // iOS Safari
  if (window.navigator.standalone === true) return true;
  // Android / Desktop Chrome
  return window.matchMedia?.("(display-mode: standalone)")?.matches === true ||
         window.matchMedia?.("(display-mode: window-controls-overlay)")?.matches === true;
}
